/**
 * The paid-plan audience model. The backend targets `SubscriptionPlan` ids; the
 * matrix groups those ids into cells of game × format × tier × billing
 * interval, using the `game` and `plan_format` the plans endpoint computes.
 * Everything the matrix, the summary, the list column and the collision check
 * need is a pure function of the cells and an id list, so the four cannot
 * disagree about what a selection means.
 */

import type {
  AnnouncementPlanFormatChoice,
  AnnouncementPlanGameChoice,
  AnnouncementPlanSchema,
  SubscriptionInterval,
  SubscriptionTier,
} from '@gto/api-client/admin/react-query'

import { humanizeToken } from '@/utils/humanize'

export const PLAN_GAMES = ['HOLDEM', 'PLO'] as const satisfies readonly AnnouncementPlanGameChoice[]
export const PLAN_FORMATS = [
  'CASH',
  'TOURNAMENT',
  'ANY',
] as const satisfies readonly AnnouncementPlanFormatChoice[]
export const PLAN_TIERS = [
  'STARTER',
  'PREMIUM',
  'ELITE',
  'ULTRA',
] as const satisfies readonly SubscriptionTier[]
export const PLAN_INTERVALS = [
  'MONTHLY',
  'YEARLY',
] as const satisfies readonly SubscriptionInterval[]

export type PlanGame = (typeof PLAN_GAMES)[number]
export type PlanFormat = (typeof PLAN_FORMATS)[number]
export type PlanTier = (typeof PLAN_TIERS)[number]
export type PlanInterval = (typeof PLAN_INTERVALS)[number]

/**
 * A game or format the backend adds must get a matrix row, or its plans would
 * silently fall in no cell. Tuple-wrapped because `never` is assignable to
 * everything.
 */
type RowsCoverBackend = [Exclude<AnnouncementPlanGameChoice, PlanGame>] extends [never]
  ? [Exclude<AnnouncementPlanFormatChoice, PlanFormat>] extends [never]
    ? true
    : false
  : false
const _rowsCoverBackend: RowsCoverBackend = true

export type PlanParts = {
  game: PlanGame
  format: PlanFormat
  tier: PlanTier
  interval: PlanInterval
}

/** One matrix cell: the plan ids the backend lists for these four values, ascending. */
export type PlanCell = PlanParts & { ids: readonly number[] }

export const GAME_LABEL: Record<PlanGame, string> = { HOLDEM: "Hold'em", PLO: 'PLO' }
export const FORMAT_LABEL: Record<PlanFormat, string> = {
  CASH: 'Cash',
  TOURNAMENT: 'Tournaments',
  ANY: 'All formats',
}
export const PLAN_TIER_LABEL: Record<PlanTier, string> = {
  STARTER: 'Starter',
  PREMIUM: 'Premium',
  ELITE: 'Elite',
  ULTRA: 'Ultra',
}
export const PLAN_INTERVAL_LABEL: Record<PlanInterval, string> = {
  MONTHLY: 'Monthly',
  YEARLY: 'Annual',
}

/**
 * The matrix name of a tier. The backend lists tiers the matrix has no column
 * for, such as `TRIAL`. A plan or an audience with one still needs a name, so
 * the value reads as a word: `TRIAL` → `Trial`.
 */
export const tierLabel = (tier: string): string => {
  const known = PLAN_TIERS.find((choice) => choice === tier)

  return known === undefined ? humanizeToken(tier) : PLAN_TIER_LABEL[known]
}

// The backend lists intervals the matrix has no column for, such as `DAILY`. A
// plan with one still needs a name, so the value reads as a word: `DAILY` → `Daily`.
const intervalLabel = (interval: string): string => {
  const known = PLAN_INTERVALS.find((choice) => choice === interval)

  return known === undefined ? humanizeToken(interval) : PLAN_INTERVAL_LABEL[known]
}

/**
 * `Hold'em Cash · Premium Annual`: the four dimensions the matrix places a plan
 * by, so every screen that names a plan names the same cell the matrix shows.
 */
export const planLabel = (plan: AnnouncementPlanSchema): string =>
  `${GAME_LABEL[plan.game]} ${FORMAT_LABEL[plan.plan_format]} · ${tierLabel(plan.tier)} ${intervalLabel(plan.interval)}`

/**
 * Ids ascending and without duplicates. The backend reads them back sorted, so
 * a stable order keeps a no-op save from turning into a PATCH that rewrites
 * the list with the same contents.
 */
export const sortedIds = (ids: Iterable<number>): number[] =>
  [...new Set(ids)].sort((a, b) => a - b)

/**
 * Every cell in matrix reading order: rows first (game, then format), then
 * columns (tier, then interval). A cell no listed plan falls in has no ids.
 */
export const planCells = (plans: readonly AnnouncementPlanSchema[]): PlanCell[] =>
  PLAN_GAMES.flatMap((game) =>
    PLAN_FORMATS.flatMap((format) =>
      PLAN_TIERS.flatMap((tier) =>
        PLAN_INTERVALS.map((interval) => ({
          game,
          format,
          tier,
          interval,
          ids: sortedIds(
            plans
              .filter(
                (plan) =>
                  plan.game === game &&
                  plan.plan_format === format &&
                  plan.tier === tier &&
                  plan.interval === interval,
              )
              .map((plan) => plan.id),
          ),
        })),
      ),
    ),
  )

/** A partial cell: every dimension it names must match, every dimension it omits matches anything. */
export type PlanFilter = Partial<PlanParts>

const cellMatches = (cell: PlanParts, filter: PlanFilter): boolean =>
  (filter.game === undefined || filter.game === cell.game) &&
  (filter.format === undefined || filter.format === cell.format) &&
  (filter.tier === undefined || filter.tier === cell.tier) &&
  (filter.interval === undefined || filter.interval === cell.interval)

/** The ids of every cell the filter matches, ascending. */
export const idsMatching = (cells: readonly PlanCell[], filter: PlanFilter): number[] =>
  sortedIds(cells.filter((cell) => cellMatches(cell, filter)).flatMap((cell) => cell.ids))

export const cellAt = (cells: readonly PlanCell[], parts: PlanParts): PlanCell | undefined =>
  cells.find(
    (cell) =>
      cell.game === parts.game &&
      cell.format === parts.format &&
      cell.tier === parts.tier &&
      cell.interval === parts.interval,
  )

/** A cell is chosen when it has plans and every one of them is selected. */
export const isCellChosen = (cell: PlanCell, selected: readonly number[]): boolean =>
  cell.ids.length > 0 && cell.ids.every((id) => selected.includes(id))

/** Selected ids no cell holds. The matrix cannot show them, so every edit keeps them. */
export const idsOutsideCells = (
  selected: readonly number[],
  cells: readonly PlanCell[],
): number[] => {
  const inCells = new Set(cells.flatMap((cell) => cell.ids))

  return selected.filter((id) => !inCells.has(id))
}

export type GroupState = 'all' | 'some' | 'none'

export const groupState = (selected: readonly number[], group: readonly number[]): GroupState => {
  // An empty line (no plan in it) is `none`, never vacuously `all`.
  if (group.length === 0) {
    return 'none'
  }
  const chosen = new Set(selected)
  const count = group.filter((id) => chosen.has(id)).length
  if (count === 0) {
    return 'none'
  }

  return count === group.length ? 'all' : 'some'
}

/** Turns every given id on or off, and returns the selection ascending. */
export const setPlans = (
  selected: readonly number[],
  ids: readonly number[],
  on: boolean,
): number[] => {
  const next = new Set(selected)
  for (const id of ids) {
    if (on) {
      next.add(id)
    } else {
      next.delete(id)
    }
  }

  return sortedIds(next)
}

/** A fully selected group clears; any other group becomes fully selected. */
export const toggleGroup = (selected: readonly number[], group: readonly number[]): number[] =>
  setPlans(selected, group, groupState(selected, group) !== 'all')

export const sameSelection = (a: readonly number[], b: readonly number[]): boolean => {
  const left = new Set(a)
  const right = new Set(b)

  return left.size === right.size && [...left].every((id) => right.has(id))
}

/** The plan ids a stored row targets, ascending. */
export const storedPlans = (row: { audience_plans?: readonly number[] | null }): number[] =>
  sortedIds(row.audience_plans ?? [])

type Row = Pick<PlanParts, 'game' | 'format'>
type Column = Pick<PlanParts, 'tier' | 'interval'>

const ALL_COLUMNS: readonly Column[] = PLAN_TIERS.flatMap((tier) =>
  PLAN_INTERVALS.map((interval) => ({ tier, interval })),
)

const sameRow = (a: Row, b: Row): boolean => a.game === b.game && a.format === b.format

/** One rectangle of the selection: these columns, each covering exactly these rows. */
type PlanRectangle = { rows: Row[]; columns: Column[] }

/** The rows that hold at least one plan, in matrix order. */
const filledRows = (cells: readonly PlanCell[]): Row[] =>
  PLAN_GAMES.flatMap((game) =>
    PLAN_FORMATS.filter((format) =>
      cells.some((cell) => cell.game === game && cell.format === format && cell.ids.length > 0),
    ).map((format) => ({ game, format })),
  )

/**
 * Factor the chosen cells into rectangles by grouping the tier × interval
 * columns that cover the exact same set of rows. Each rectangle then reads as
 * one plain sentence.
 */
const factor = (chosen: readonly PlanCell[], rows: readonly Row[]): PlanRectangle[] => {
  const rectangles: PlanRectangle[] = []
  for (const column of ALL_COLUMNS) {
    const covered = rows.filter((row) =>
      chosen.some(
        (cell) =>
          sameRow(cell, row) && cell.tier === column.tier && cell.interval === column.interval,
      ),
    )
    if (covered.length === 0) {
      continue
    }
    const rectangle = rectangles.find(
      (candidate) =>
        candidate.rows.length === covered.length &&
        candidate.rows.every((row, index) => {
          const other = covered[index]

          return other !== undefined && sameRow(row, other)
        }),
    )
    if (rectangle === undefined) {
      rectangles.push({ rows: covered, columns: [column] })
    } else {
      rectangle.columns.push(column)
    }
  }

  return rectangles
}

const describeRows = (rows: readonly Row[], allRows: readonly Row[]): string | null => {
  if (rows.length === allRows.length) {
    return null
  }

  return PLAN_GAMES.flatMap((game) => {
    const available = allRows.filter((row) => row.game === game)
    const have = PLAN_FORMATS.filter((format) => rows.some((row) => sameRow(row, { game, format })))
    if (have.length === 0) {
      return []
    }

    return have.length === available.length
      ? [GAME_LABEL[game]]
      : [`${GAME_LABEL[game]} ${have.map((format) => FORMAT_LABEL[format]).join(' / ')}`]
  }).join(' + ')
}

const describeColumns = (columns: readonly Column[]): string => {
  if (columns.length === ALL_COLUMNS.length) {
    return 'Any tier'
  }
  const perTier = PLAN_TIERS.map((tier) => ({
    tier,
    intervals: PLAN_INTERVALS.filter((interval) =>
      columns.some((column) => column.tier === tier && column.interval === interval),
    ),
  })).filter((entry) => entry.intervals.length > 0)
  const first = perTier[0]
  if (first === undefined) {
    return ''
  }
  const sameIntervals = perTier.every((entry) => entry.intervals.join() === first.intervals.join())
  if (sameIntervals) {
    const tiers =
      perTier.length === PLAN_TIERS.length
        ? 'All tiers'
        : perTier.map((entry) => PLAN_TIER_LABEL[entry.tier]).join(', ')
    const [onlyInterval] = first.intervals
    const intervals =
      first.intervals.length === PLAN_INTERVALS.length || onlyInterval === undefined
        ? ''
        : ` · ${PLAN_INTERVAL_LABEL[onlyInterval]}`

    return `${tiers}${intervals}`
  }

  return perTier
    .map((entry) => {
      const [onlyInterval] = entry.intervals

      return entry.intervals.length === PLAN_INTERVALS.length || onlyInterval === undefined
        ? PLAN_TIER_LABEL[entry.tier]
        : `${PLAN_TIER_LABEL[entry.tier]} ${PLAN_INTERVAL_LABEL[onlyInterval]}`
    })
    .join(', ')
}

/** The plain count, for a selection the cells cannot describe. */
export const planCountLabel = (count: number): string => (count === 1 ? '1 plan' : `${count} plans`)

export type PlanSummary =
  | { kind: 'unfiltered' }
  | { kind: 'every'; count: number; total: number }
  | { kind: 'some'; sentences: string[]; count: number; total: number }

/**
 * The plain-language reading of a selection, counted in cells. Empty means no
 * plan filter, which the backend treats as every paid plan.
 */
export const summarizePlans = (
  selected: readonly number[],
  cells: readonly PlanCell[],
): PlanSummary => {
  if (selected.length === 0) {
    return { kind: 'unfiltered' }
  }
  const filled = cells.filter((cell) => cell.ids.length > 0)
  const chosen = filled.filter((cell) => isCellChosen(cell, selected))
  if (filled.length > 0 && chosen.length === filled.length) {
    return { kind: 'every', count: filled.length, total: filled.length }
  }
  const allRows = filledRows(cells)
  const sentences = factor(chosen, allRows).map((rectangle) => {
    const rows = describeRows(rectangle.rows, allRows)
    const columns = describeColumns(rectangle.columns)

    return rows === null ? `${columns} — every game & format` : `${columns} — ${rows} only`
  })

  // Ids that fill no whole cell (unlisted ids, or part of a multi-plan cell)
  // factor into no rectangle; the count still says the filter is there.
  return {
    kind: 'some',
    sentences: sentences.length > 0 ? sentences : [planCountLabel(selected.length)],
    count: chosen.length,
    total: filled.length,
  }
}

/** One line for the list column. */
export const describePlans = (selected: readonly number[], cells: readonly PlanCell[]): string => {
  const summary = summarizePlans(selected, cells)
  if (summary.kind === 'unfiltered' || summary.kind === 'every') {
    return 'Every paid plan'
  }

  return summary.sentences.join('; ')
}
